<script setup lang="ts">
import { computed } from "vue";
import {
  convertSecondsFromStringTime,
  formatTimeFromSeconds,
} from "@/services/functions";

import OutputContentType from "@/types/apiresponses/jobStatusOutputContentsMonthlyData"
import AlertSettingType from "@/types/AlertSetting"

const props = defineProps<{
  detailDataRef: OutputContentType;
  alertSetting: AlertSettingType;
}>();

// 明細、小計の時間はアラートLv3をチェックしない
const restraintTimeStyle = computed(() => {
  const restraintTimeSeconds = totalByTrip.value;
  if (!restraintTimeSeconds) return "color: black;";

  const alertLv3TimeSeconds = convertSecondsFromStringTime(
    props.alertSetting?.alertLv3Time
  );
  if (
    props.alertSetting?.alertLv3Time &&
    restraintTimeSeconds > alertLv3TimeSeconds
  ) {
    return "color: " + props.alertSetting?.alertLv3Color;
  }

  const alertLv2TimeSeconds = convertSecondsFromStringTime(
    props.alertSetting?.alertLv2Time
  );
  if (
    props.alertSetting?.alertLv2Time &&
    restraintTimeSeconds > alertLv2TimeSeconds
  ) {
    return "color: " + props.alertSetting?.alertLv2Color;
  }

  const alertLv1TimeSeconds = convertSecondsFromStringTime(
    props.alertSetting?.alertLv1Time
  );
  if (
    props.alertSetting?.alertLv1Time &&
    restraintTimeSeconds > alertLv1TimeSeconds
  ) {
    return "color: " + props.alertSetting?.alertLv1Color;
  }

  return "color: black;";
});

const summaryTargetTrips = computed(() => {
  const trips = [];
  const cp = JSON.parse(JSON.stringify(props.detailDataRef));
  cp.forEach((detailData) => {
    if (!detailData) return;
    (detailData?.trips ?? []).forEach((trip) => {
      trips.push(trip);
    });
  });
  return trips;
});

const summary = (key) => {
  let sum = 0;
  summaryTargetTrips.value.forEach((trip) => {
    sum += trip[key];
  });
  return sum;
};

const totalByTrip = computed(() => {
  const sum = summary("total_by_trip");
  const diff = summary("leaving_diff_time");
  return sum - diff;
});

// show formatted time

const formattedTotalByTrip = computed(() => {
  return formatTimeFromSeconds(totalByTrip.value);
});

const formattedWorkingTime = computed(() => {
  const sum = summary("working_time");
  return formatTimeFromSeconds(sum);
});

const formattedTotalMileage = computed(() => {
  const sum = summary("total_mileage");
  return Math.round(sum);
});

const formattedDriving = computed(() => {
  const sum = summary("driving");
  return formatTimeFromSeconds(sum);
});

const formattedBreak = computed(() => {
  const sum = summary("break");
  return formatTimeFromSeconds(sum);
});

const formattedLoadingTime = computed(() => {
  const sum = summary("loading_time");
  return formatTimeFromSeconds(sum);
});

const formattedUnloadingTime = computed(() => {
  const sum = summary("unloading_time");
  return formatTimeFromSeconds(sum);
});

const formattedShippingTime = computed(() => {
  const sum = summary("shipping_time");
  return formatTimeFromSeconds(sum);
});

const formattedWaitTime = computed(() => {
  const sum = summary("wait_time");
  return formatTimeFromSeconds(sum);
});

const formattedOthersTime = computed(() => {
  const sum = summary("others_time");
  return formatTimeFromSeconds(sum);
});

const formattedRestTime = computed(() => {
  const sum = summary("rest");
  return formatTimeFromSeconds(sum);
});

const formattedLeavingDiffTime = computed(() => {
  const sum = summary("leaving_diff_time");
  return formatTimeFromSeconds(sum);
});

const formattedOverTime = computed(() => {
  const sum = summary("over_time");
  return formatTimeFromSeconds(sum);
});

const formattedOverNightTime = computed(() => {
  const sum = summary("over_night_time");
  return formatTimeFromSeconds(sum);
});
</script>

<template>
  <tr>
    <td :style="restraintTimeStyle">
      {{ formattedTotalByTrip }}
    </td>
    <td>{{ formattedWorkingTime }}</td>
    <td>{{ formattedTotalMileage }}</td>
    <td>{{ formattedDriving }}</td>
    <td>{{ formattedBreak }}</td>
    <td>{{ formattedLoadingTime }}</td>
    <td>{{ formattedUnloadingTime }}</td>
    <td>{{ formattedShippingTime }}</td>
    <td>{{ formattedWaitTime }}</td>
    <td>{{ formattedOthersTime }}</td>
    <td>{{ formattedRestTime }}</td>
    <td>{{ formattedLeavingDiffTime }}</td>
    <td>{{ formattedOverTime }}</td>
    <td>{{ formattedOverNightTime }}</td>
  </tr>
</template>

<style scoped>
.summary-times-container td {
  padding: 7px;
  text-align: center;
  font-weight: bold !important;
  border: 1 !important;
  border-width: 2px !important;
}
.summary-times-container .table-header {
  background-color: silver !important;
}
.summary-times-container tr {
  line-height: 15px;
  font-size: 13px;
}
</style>
